import { useCallback, memo } from "react";
import CustomSelect from "../../leadSettings/components/CustomSelect";
import { isFieldVisible } from "../../leadSettings/fieldRuleUtils";

// Individual field component for better isolation
const FormField = memo(({ field, value, error, onChange, statuses }) => {
  const fieldId = String(field.id || field._id);
  
  const handleChange = useCallback(
    (newValue) => {
      onChange(fieldId, newValue);
    },
    [fieldId, onChange]
  );

  if (field.type === "textarea") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={fieldId} className="text-sm font-semibold text-slate-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <textarea
          id={fieldId}
          name={fieldId}
          className={`h-24 rounded-2xl border px-3 py-2 text-sm focus:outline-none focus:border-[#3f8cff] ${
            error ? "border-red-300" : "border-slate-200"
          }`}
          placeholder={field.placeholder || `Enter ${field.label}`}
          value={value || ""}
          onChange={(e) => {
            e.stopPropagation();
            handleChange(e.target.value);
          }}
        />
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={fieldId} className="text-sm font-semibold text-slate-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <CustomSelect
          value={value || ""}
          onChange={handleChange}
          options={[
            { value: "", label: `Select ${field.label}` },
            ...(field.options || []).map((option) => {
              const val = typeof option === "object" ? (option.value || option.id || option.name) : option;
              const lbl = typeof option === "object" ? (option.label || option.name || option.id) : option;
              return {
                value: val,
                label: lbl,
              };
            }),
          ]}
        />
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={fieldId} className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            id={fieldId}
            name={fieldId}
            checked={!!value}
            onChange={(e) => {
              e.stopPropagation();
              handleChange(e.target.checked);
            }}
            className="h-4 w-4 rounded border-slate-300 text-[#3f8cff] focus:ring-[#3f8cff]"
          />
          {field.label}
          {field.required && <span className="text-red-500">*</span>}
        </label>
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  // Handle special field: status
  if (fieldId === "status" && statuses) {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={fieldId} className="text-sm font-semibold text-slate-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <CustomSelect
          value={value || ""}
          onChange={handleChange}
          options={[
            { value: "", label: "Select Status" },
            ...statuses.map((status) => ({
              value: status._id || status.id,
              label: status.name,
            })),
          ]}
        />
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  // Default input field (text, email, tel, number)
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="text-sm font-semibold text-slate-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={field.type}
        id={fieldId}
        name={fieldId}
        className={`h-11 rounded-2xl border px-3 text-sm focus:outline-none focus:border-[#3f8cff] ${
          error ? "border-red-300" : "border-slate-200"
        }`}
        placeholder={field.placeholder || `Enter ${field.label}`}
        value={value || ""}
        onChange={(e) => {
          e.stopPropagation();
          handleChange(e.target.value);
        }}
      />
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
});

FormField.displayName = "FormField";

import { useRef, useState, useEffect } from "react";

// Animated wrapper — slides the field in/out when its visibility changes
const AnimatedField = ({ visible, children, zIndex }) => {
  const [rendered, setRendered] = useState(visible);
  const [animating, setAnimating] = useState(visible);
  const [fullyOpen, setFullyOpen] = useState(visible);
  const timerRef = useRef(null);
  const overflowTimerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setRendered(true);
      timerRef.current = setTimeout(() => {
        setAnimating(true);
        overflowTimerRef.current = setTimeout(() => setFullyOpen(true), 250);
      }, 10);
    } else {
      setFullyOpen(false);
      setAnimating(false);
      timerRef.current = setTimeout(() => setRendered(false), 280);
    }
    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(overflowTimerRef.current);
    };
  }, [visible]);

  if (!rendered) return null;

  return (
    <div
      style={{
        position: "relative",
        zIndex: zIndex,
        overflow: fullyOpen ? "visible" : "hidden",
        transition: "max-height 0.25s ease, opacity 0.25s ease, transform 0.25s ease",
        maxHeight: animating ? "1000px" : "0px",
        opacity: animating ? 1 : 0,
        transform: animating ? "translateY(0)" : "translateY(-6px)",
      }}
    >
      {children}
    </div>
  );
};

const DynamicLeadForm = ({ fields, values, onChange, errors, statuses }) => {
  const handleFieldChange = useCallback(
    (fieldId, newValue) => {
      if (typeof onChange === "function" && fieldId) {
        // Create a new object with the updated field value
        onChange((prev) => {
          const currentValues = prev && typeof prev === "object" ? prev : {};
          return {
            ...currentValues,
            [String(fieldId)]: newValue,
          };
        });
      }
    },
    [onChange]
  );

  return (
    <div className="space-y-4">
      {fields.map((field, index) => {
        const id = field.id || field._id;
        if (!id) {
          console.warn("Field missing id:", field);
          return null;
        }

        const fieldId = String(id);
        const fieldValue = values?.[fieldId] ?? "";
        const fieldError = errors?.[fieldId];
        const visible = isFieldVisible(field, values);
        
        if (!visible) return null;

        return (
          <AnimatedField key={fieldId} visible={visible} zIndex={fields.length - index}>
            <FormField
              field={field}
              value={fieldValue}
              error={fieldError}
              onChange={handleFieldChange}
              statuses={statuses}
            />
          </AnimatedField>
        );
      })}
    </div>
  );
};

export default DynamicLeadForm;
